import image from "./img/image.png";
import { Linkedin, Github,Mail, ChevronRight } from "lucide-react";
import datagpt from "./img/datagpt.png";
import dash5 from "./img/dash5.png";
import churn from "./img/churn.png";
import { useEffect } from "react";
import { Link, HashRouter, Routes, Route, useSearchParams } from "react-router-dom";

const projects = [
  {
    id: "kick-detection", title: "Kick Detection in Oil & Gas Drilling", featured: true,
    image: dash5, category: "Time-series machine learning", status: "Research & dashboard",
    tags: ["Python", "TensorFlow", "Streamlit", "Scikit-learn"],
    summary: "Developed an LSTM-autoencoder pipeline to identify anomalies in drilling sensor data, with a Streamlit dashboard for monitoring and decision support.",
    approach: "Prepared time-series sensor data, trained an LSTM autoencoder and visualised model outputs alongside drilling measurements.",
    evidence: "The repository includes the modelling workflow and dashboard. The underlying industrial dataset is proprietary and cannot be shared publicly.",
    limitation: "Performance depends on the drilling environment. Public reproduction requires a suitable replacement dataset; operational deployment needs further validation.",
    repo: "LSTM-autoencoder"
  },
  {
    id: "sql-analytics", title: "E-commerce Sales & Customer Analytics", featured: true,
    category: "SQL & commercial analytics", status: "Portfolio project",
    tags: ["PostgreSQL", "SQL", "Python", "Streamlit", "Docker"],
    summary: "Transformed 541,909 retail transaction lines into a relational analytics model, with sales KPIs, RFM customer segments and cohort retention analysis.",
    approach: "Built customers, orders, order items and products tables; added data quality checks and analytical queries using CTEs and window functions.",
    evidence: "The documented analysis reports £10.67m in completed-sales revenue and 654 at-risk customers with £898.6k in historical revenue. A Streamlit dashboard presents the results.",
    limitation: "Findings describe the historical UCI Online Retail dataset. At-risk segments suggest retention opportunities, rather than measured campaign impact.",
    repo: "ecommerce-sql-analytics"
  },
  {
    id: "ai-analyst", title: "AI Powered Data Analyst Assistant", featured: true,
    image: datagpt, category: "AI application", status: "Interactive application",
    tags: ["Python", "OpenAI", "LangChain", "Streamlit", "Plotly"],
    summary: "Built an assistant that accepts CSV files and turns natural-language questions into data analysis and interactive charts using LangChain, OpenAI and Plotly.",
    approach: "Connected a conversational interface to a Pandas analysis workflow, with chart rendering in Streamlit.",
    evidence: "Example tasks include distributions, group comparisons and correlation questions on structured CSV data.",
    limitation: "Generated analysis needs verification. Results depend on data quality and question clarity. Use non-sensitive sample data when trying the hosted demo.",
    repo: "ai-data-analyst", demo: "https://ai-data-analyst-nkosimate.streamlit.app/"
  },
  {
    id: "climate-risk", title: "Climate & Housing Risk Explorer",
    category: "Geospatial data engineering", status: "Prototype · demo unavailable",
    tags: ["Python", "GeoPandas", "Azure", "Mapbox"],
    summary: "Built a multi-source geospatial pipeline combining flood exposure, coastal erosion, migration and property price trends into an exploratory area-level index.",
    approach: "Used postcode spatial joins, processed Parquet layers and Azure Data Lake storage to prepare data for a choropleth map.",
    evidence: "The repository documents ingestion, spatial processing, weighted scoring and Azure upload stages.",
    limitation: "Heat risk currently uses a neutral placeholder in the scoring code. Coverage, missing-data treatment and weights require validation. The hosted demo is currently unavailable.",
    repo: "climate-house-risk"
  },
  {
    id: "fraud-detection", title: "Cost-Sensitive Credit Card Fraud Detection",
    category: "Classification & explainability", status: "Research notebook",
    tags: ["Python", "Scikit-learn", "XGBoost", "SHAP"],
    summary: "Explored fraud detection under extreme class imbalance, using time-aware evaluation, precision–recall analysis and cost-sensitive decision thresholds.",
    approach: "Compared Logistic Regression, Random Forest and XGBoost, with class weighting and SHAP explanations.",
    evidence: "The notebook explains model selection, threshold trade-offs and monitoring considerations.",
    limitation: "Transaction costs are illustrative assumptions. Production monitoring and real-time scoring are proposed next steps, rather than deployed capabilities.",
    repo: "fraud-detection"
  },
  {
    id: "churn", title: "Customer Churn Prediction",
    image: churn, category: "Customer analytics & machine learning", status: "Research notebook",
    tags: ["Python", "Pandas", "Scikit-learn", "Gradient Boosting"],
    summary: "Built a telecom churn modelling workflow with exploratory analysis, behavioural feature engineering and model tuning to identify customers at risk of leaving.",
    approach: "Compared baseline classifiers and tuned Gradient Boosting, examining features such as UsagePerDollar and DataMismatch.",
    evidence: "The project explores service calls, charges and usage patterns as potential churn drivers.",
    limitation: "Model metrics and dataset documentation need reconciliation before making quantified performance claims. Retention impact has not been measured.",
    repo: "CustomerChurnPrediction"
  },
  {
    id: "pneumonia", title: "Pneumonia Detection from Chest X-rays",
    category: "Computer vision", status: "Research notebook",
    tags: ["Python", "TensorFlow", "Keras", "InceptionV3"],
    summary: "Compared a custom CNN with InceptionV3 transfer learning for binary classification of chest X-ray images.",
    approach: "Explored preprocessing, augmentation, fine-tuning and evaluation with confusion matrices and ROC analysis.",
    evidence: "The notebook documents the baseline and transfer-learning experiments.",
    limitation: "An educational research project. Results do not establish clinical suitability or generalisation to other patient populations.",
    repo: "Pneumonia-Detection"
  },
  {
    id: "warehouse", title: "Northwind Data Warehouse & ETL",
    category: "Business intelligence & data engineering", status: "Coursework project",
    tags: ["SQL Server", "SSIS", "SSAS", "MDX", "PowerPivot"],
    summary: "Designed a star-schema warehouse and ETL workflow for sales analysis, supported by an OLAP cube and MDX queries.",
    approach: "Modelled fact and dimension tables, built SSIS packages and explored sales through SSAS and PowerPivot.",
    evidence: "The repository includes SQL scripts, ETL packages, cube definitions, MDX queries and a PowerPivot workbook.",
    limitation: "Requires the Microsoft SQL Server toolchain and source database setup; there is no hosted demonstration.",
    repo: "Data-Warehousing"
  }
];

function ProjectCard({ project, detailed = false }) {
  return (
    <article id={project.id} className="bg-gray-50 p-6 rounded-lg shadow-md border border-blue-100">
      <div className="flex flex-col md:flex-row items-start gap-6">
        {project.image && <img src={project.image} alt={`${project.title} project preview`} className="w-full md:w-2/5 rounded-lg object-contain shadow-sm" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-blue-700 mb-2">{project.category} · {project.status}</p>
          {detailed ? <h2 className="text-xl font-semibold mb-3 text-blue-800">{project.title}</h2> : <h3 className="text-xl font-semibold mb-3 text-blue-800">{project.title}</h3>}
          <div className="flex flex-wrap gap-2 mb-3">
            {project.tags.map(tag => <span key={tag} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">{tag}</span>)}
          </div>
          <p className="text-gray-700 leading-relaxed mb-4 text-sm">{project.summary}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-3 text-sm font-semibold">
            <a href={`https://github.com/nkosimate/${project.repo}`} aria-label={`${project.title} on GitHub`} className="inline-flex items-center text-blue-700 hover:underline">GitHub <ChevronRight size={16} aria-hidden="true" /></a>
            {project.demo && <a href={project.demo} className="inline-flex items-center text-blue-700 hover:underline">Live demo <ChevronRight size={16} aria-hidden="true" /></a>}
            {!detailed && <Link to={`/projects?project=${project.id}`} className="text-blue-700 hover:underline">View case study</Link>}
          </div>
        </div>
      </div>
      {detailed && <dl className="mt-6 space-y-4 border-t border-blue-100 pt-5 text-sm leading-relaxed">
        <div><dt className="font-semibold text-blue-900">Approach</dt><dd className="text-gray-700">{project.approach}</dd></div>
        <div><dt className="font-semibold text-blue-900">Evidence & findings</dt><dd className="text-gray-700">{project.evidence}</dd></div>
        <div><dt className="font-semibold text-blue-900">Current limitations</dt><dd className="text-gray-700">{project.limitation}</dd></div>
      </dl>}
    </article>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 font-inter text-gray-800 antialiased p-4 sm:p-8 md:p-12">
      <div className="container mx-auto flex flex-col md:flex-row gap-8 rounded-xl shadow-2xl overflow-hidden bg-white">
        <aside className="md:w-1/3 lg:w-1/4 p-6 md:p-8 bg-blue-700 text-white flex flex-col items-center text-center rounded-t-xl md:rounded-l-xl md:rounded-tr-none">
          <img
            src={image}
            alt="Nkosinathi Mate"
            className="w-28 h-28 rounded-full mb-4 ring-4 ring-blue-300 object-cover transform transition-transform duration-300 hover:scale-105"
          />
          <h1 className="text-3xl font-bold mb-1">Nkosinathi Mate</h1>
          <p className="text-blue-200 text-lg mb-2">
            Graduate Data Scientist and Software Engineer
          </p>
          <p className="text-blue-300 text-sm mb-6">Aberdeen, United Kingdom</p>

          <div className="flex justify-center items-center space-x-4 mb-8">
            <a
              href="https://www.linkedin.com/in/nkosinathi-mate/"
              aria-label="LinkedIn profile"
              className="p-2 bg-blue-600 rounded-full hover:bg-blue-500 transition-colors duration-200"
            >
              <Linkedin size={24} strokeWidth={1.5} />
            </a>
            <a
              href="https://github.com/nkosimate"
              aria-label="GitHub profile"
              className="p-2 bg-blue-600 rounded-full hover:bg-blue-500 transition-colors duration-200"
            >
              <Github size={24} strokeWidth={1.5} />
            </a>
            <a
              href="mailto:nkosimate@gmail.com"
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 rounded-full text-sm font-semibold hover:bg-blue-400 transition-colors duration-200 shadow-md"
            >
              <Mail size={18} strokeWidth={1.5} />
              <span>Email Me</span>
            </a>
          </div>

          <section className="bg-blue-800 bg-opacity-30 p-6 rounded-lg mb-6 shadow-inner w-full">
            <h2 className="text-xl font-semibold mb-3 border-b border-blue-600 pb-2">
              About
            </h2>
            <p className="text-blue-100 leading-relaxed text-sm">
              Data science and analytics across industrial safety, commercial
              analysis and AI applications. MSc Distinction. Python, SQL
              and clear communication. Open to relocation.
            </p>
          </section>

          <section className="bg-blue-800 bg-opacity-30 p-6 rounded-lg shadow-inner w-full">
            <h2 className="text-xl font-semibold mb-3 border-b border-blue-600 pb-2">
              Skills
            </h2>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                "SQL",
                "Python",
                "R",
                "Tableau",
                "Excel",
                "AWS",
                "Statistical modelling",
                "Scikit-learn",
                "TensorFlow/PyTorch",
                "Streamlit",
                "Git",
                "Hadoop",
                "PySpark",
                "Apache Pig",
                "Feature Engineering & Model Evaluation",
              ].map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-600 text-blue-100 rounded-full text-xs font-medium shadow-sm transition-transform duration-200 hover:scale-105 hover:bg-blue-500"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        </aside>

        <section className="flex-1 p-6 md:p-10 lg:p-12 bg-white rounded-b-xl md:rounded-r-xl md:rounded-bl-none">
          <section className="mb-10">
            <div className="flex items-center justify-between gap-4 mb-6 border-b-2 border-blue-200 pb-3">
              <h2 className="text-3xl font-bold text-blue-700">Projects</h2>
              <Link
                to="/projects"
                className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800"
              >
                More Projects <ChevronRight size={16} className="ml-1" />
              </Link>
            </div>

            <div className="space-y-8">
              {projects.filter(project => project.featured).map(project => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-3xl font-bold text-blue-700 mb-6 border-b-2 border-blue-200 pb-3">
              Experience
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <p className="text-sm text-gray-500 mb-1">
                March 2024 – August 2024
              </p>
              <h3 className="text-xl font-semibold mb-2 text-blue-800">
                Data Scientist Intern at SafeInflux
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                At SafeInflux, I spearheaded the development of an
                LSTM-AE-driven anomaly detection system for drilling operations,
                evaluated on drilling sensor data. My role encompassed
                end-to-end ML pipeline creation, from engineering and
                pre-processing diverse time-series sensor data to deploying a
                Streamlit dashboard that empowered engineers with real-time
                insights, to support operational decision-making. I also
                ensured pipeline scalability and reproducibility through
                meticulous documentation and Git version control.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  "Python",
                  "Pandas",
                  "Streamlit",
                  "Excel",
                  "Tensorflow",
                  "Scikit-learn",
                ].map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-blue-700 mb-6 border-b-2 border-blue-200 pb-3">
              Education
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <p className="text-sm text-gray-500 mb-1">December 2024</p>
              <h3 className="text-xl font-semibold mb-2 text-blue-800">
                MSc Data Science, Robert Gordon University, Aberdeen
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Master of Science in Data Science from Robert Gordon University,
                providing a robust foundation in designing and implementing
                end-to-end data solutions, from Big Data acquisition,
                warehousing, and advanced analytics to impactful visualization.
                The program uniquely emphasizes translating key performance
                indicators into actionable business decisions, equipping me with
                the skills to drive strategic insights in diverse industries.
              </p>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

function ProjectsPage() {
  const [searchParams] = useSearchParams();
  const selectedProject = searchParams.get("project");
  useEffect(() => {
    if (selectedProject) document.getElementById(selectedProject)?.scrollIntoView?.({ block: "start" });
    else window.scrollTo?.(0, 0);
  }, [selectedProject]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 p-6 sm:p-10 md:p-12">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 shadow-2xl">
        <Link to="/" className="mb-6 inline-flex items-center text-blue-600 hover:text-blue-800">
          ← Back Home
        </Link>
        <h1 className="mb-4 text-4xl font-bold text-blue-700">Projects</h1>
        <p className="mb-8 text-gray-700">
          A closer look at the data science and software projects I have built.
        </p>
        <p className="mb-8 text-sm text-gray-600">
          Explore commercial analytics, machine learning and data engineering.
          Each project includes its approach, evidence and current limitations.
        </p>
        <div className="space-y-6">
          {projects.map(project => <ProjectCard key={project.id} project={project} detailed />)}
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/projects" element={<ProjectsPage />} />
      </Routes>
    </HashRouter>
  );
}

export default App;
